import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import { getEnrollment } from '../../api/enrollments/get-enrollment'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import ClientSpaceHeaderWrapper from '../../components/pages/client-space/client-space-header-wrapper'
import { Paths } from '../../lib/constants/path'
import EnrollmentProvider from '../../lib/contexts/enrollment'
import { useObserver } from '../../lib/contexts/observer'
import useAuth from '../../lib/hooks/use-auth'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import {
  enrollmentClientContextFields,
  enrollmentClientContextJourneyFields,
  enrollmentClientContextMainFields
} from '../../lib/types/fields/enrollments/enrollment'
import ClientPageNotFound from '../common/client-page-not-found'

export default function ClientPersonalSpaceLayout() {
  const { id } = useParams()
  const enrollmentId = Number(id)
  const hasEnrollmentId = Number.isInteger(enrollmentId)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: enrollment, isLoading } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentClientContextFields],
    queryFn: async () => {
      const [mainDetails, journeyDetails] = await Promise.all([
        getEnrollment(enrollmentId, enrollmentClientContextMainFields),
        getEnrollment(enrollmentId, enrollmentClientContextJourneyFields)
      ])

      return { ...mainDetails, ...journeyDetails }
    },
    enabled: user?.role === RoleEnum.CLIENT && hasEnrollmentId
  })

  useObserver({
    key: 'enrollment-client-personal-space',
    update: () =>
      queryClient.invalidateQueries({
        queryKey: ['enrollment', enrollmentId, enrollmentClientContextFields]
      })
  })

  if (!hasEnrollmentId) return <Navigate to={Paths.CLIENT_PERSONAL_SPACE_REDIRECTION} replace />
  if (isLoading) return <LoadingScreen />
  if (!enrollment) return <ClientPageNotFound />

  return (
    <EnrollmentProvider enrollment={enrollment}>
      <ClientSpaceHeaderWrapper enrollment={enrollment}>
        <Outlet />
      </ClientSpaceHeaderWrapper>
    </EnrollmentProvider>
  )
}
